import React, { useEffect } from "react"
import { RouterProvider } from "react-router-dom"
import { router } from "./routes/index"
import { DeviceUseCase } from "./usecases/device"
import { UserUsecase } from "./usecases/user"
import { BizError } from "./models/biz_model"
import {
  LoginStatus,
  setPublicKeyObj,
  updateLoginStatus,
} from "./stores/login_slice"
import { setBrowserName } from "./stores/app_slice"
import { useAppDispatch } from "./store"
import { isChromiumBlink } from "./utils/tools"
import { ConnectionUseCase } from "@/usecases/connection"
import { AssistDialogs } from "@/widgets/AssistDialogs"
import { BlockAppInstallToast } from "@/widgets/BlockAppInstallToast"

import FingerprintJS from "@fingerprintjs/fingerprintjs"
// import FingerprintJSPro from "@fingerprintjs/fingerprintjs-pro"
import { setFpHash } from "./stores/auth_slice"
import { DataReportUsecase } from "./usecases/data_report"
import { NetworkDetectionUsecase } from "./usecases/network_detection"
import { NetworkStatusUsecase } from "./usecases/network_status"

const App: React.FC = () => {
  const dispatch = useAppDispatch()
  const setErrMsg = (msg: string) => {
    dispatch(updateLoginStatus({ msg, status: LoginStatus.error }))
  }
  useEffect(() => {
    void DataReportUsecase.reportLog("app_opened", { boot_time: Date.now() })
    NetworkStatusUsecase.start()
    NetworkDetectionUsecase.start()
    return () => {
      NetworkStatusUsecase.stop()
      NetworkDetectionUsecase.stop()
    }
  }, [])

  useEffect(() => {
    const fetchPublicKey = async () => {
      const publicKeyObjRes = await UserUsecase.getPublicKey()
      if (publicKeyObjRes instanceof BizError) {
        setErrMsg(publicKeyObjRes.displayErrorMsg)
        return
      }
      const publicKeyObj = publicKeyObjRes.unwrap()
      dispatch(setPublicKeyObj(publicKeyObj))
    }
    fetchPublicKey()
    const browserName = isChromiumBlink()
    dispatch(setBrowserName(browserName))
  }, [])

  // 获取浏览器指纹
  useEffect(() => {
    const setFp = async () => {
      const fp = await FingerprintJS.load()
      const { visitorId } = await fp.get()
      dispatch(setFpHash(visitorId))
      console.log("test*** visitorId: ", visitorId)
    }
    setFp()
  }, [])
  // useEffect(() => {
  //   const setFpPro = async () => {
  //     const fpPromise = FingerprintJSPro.load({
  //       apiKey: "1VF6MbeNHlagJ1zegmDU",
  //     })
  //     const fp = await fpPromise
  //     const result = await fp.get()
  //     const visitorId = result.visitorId
  //     console.log("test*** visitorId pro: ", visitorId)
  //   }
  //   setFpPro()
  // }, [])

  useEffect(() => {
    // 统一的时间戳更新方法
    let isUpdatingTs = false
    const updateUrlTs = (): void => {
      if (isUpdatingTs) return
      try {
        isUpdatingTs = true
        const url = new URL(window.location.href)
        url.searchParams.set("_ts", Date.now().toString())
        history.replaceState(null, "", url.toString())
      } catch (e) {
        console.error("updateUrlTs error: ", e)
      } finally {
        isUpdatingTs = false
      }
    }

    // 1) 首次进入/重开页面：立即更新时间戳
    updateUrlTs()

    // 2) 返回到当前页面（包括 bfcache 恢复）：通过 pageshow 触发更新时间戳
    const handlePageShow = (event: PageTransitionEvent): void => {
      // 无论是否来自 bfcache，都刷新一次时间戳，确保“返回当前页面”时为最新
      updateUrlTs()
    }
    window.addEventListener("pageshow", handlePageShow)

    // 离开当前页面（含跳转/放入 bfcache）时也更新时间戳
    const handlePageHide = (event: PageTransitionEvent): void => {
      updateUrlTs()
    }
    window.addEventListener("pagehide", handlePageHide)

    // 浏览器前进/后退
    const handlePopState = (): void => {
      updateUrlTs()
    }
    window.addEventListener("popstate", handlePopState)

    // 包装 pushState/replaceState 并派发自定义事件
    const originalPushState = history.pushState.bind(history)
    const originalReplaceState = history.replaceState.bind(history)
    const dispatchUrlChange = (type: string): void => {
      try {
        const event = new Event("urlchange") as Event & { detail?: any }
        ;(event as any).detail = { type }
        window.dispatchEvent(event)
      } catch (_) {
        // ignore
      }
    }
    ;(
      history as History & {
        pushState: History["pushState"]
        replaceState: History["replaceState"]
      }
    ).pushState = function (...args: Parameters<History["pushState"]>): any {
      const ret = originalPushState.apply(this, args)
      dispatchUrlChange("pushstate")
      return ret
    }
    ;(
      history as History & {
        pushState: History["pushState"]
        replaceState: History["replaceState"]
      }
    ).replaceState = function (
      ...args: Parameters<History["replaceState"]>
    ): any {
      const ret = originalReplaceState.apply(this, args)
      dispatchUrlChange("replacestate")
      return ret
    }
    const handleUrlChange = (): void => {
      updateUrlTs()
    }
    window.addEventListener("urlchange", handleUrlChange)

    // 处理网页关闭，刷新
    const handleUnload = (event: BeforeUnloadEvent): void => {
      // 3) 刷新/关闭/离开当前页面前：也更新时间戳
      updateUrlTs()

      // 网页关闭时关闭信令服务器连接
      ConnectionUseCase.closeSignalingServerConn()
      // 网页关闭/刷新 回收本地设备ID
      DeviceUseCase.recycleLocalDeviceId()
    }

    // 监听网页关闭事件
    window.addEventListener("beforeunload", handleUnload)

    const dispose = (): void => {
      ConnectionUseCase.closeSignalingServerConn()
      window.removeEventListener("beforeunload", handleUnload)
      // 以下是实现：
      // 1、刷新页面 2、重开页面  3、点击当前页中的某个的按钮，在当前页打开别的url，然后返回当前页面   都能实现url中带上最新时间戳
      window.removeEventListener("pageshow", handlePageShow)
      window.removeEventListener("pagehide", handlePageHide)
      window.removeEventListener("popstate", handlePopState)
      window.removeEventListener("urlchange", handleUrlChange)
      history.pushState = originalPushState
      history.replaceState = originalReplaceState
    }
    return dispose
  }, [])

  useEffect(() => {
    // App 启动时用缓存凭证建立常驻信令（不调登录接口），使登录页也能收到远程协助请求。
    // 对齐 android UserUseCase.init -> connectSignaling；无缓存凭证（未登录过）则跳过，
    // 待登录后由 connectToSignalingServer 用最新 token 重建。
    void ConnectionUseCase.connectSignaling()
  }, [])

  return (
    <>
      <RouterProvider router={router} />
      {/* 远程协助弹窗：全局常驻，登录页与远程页均可显示（对齐 android） */}
      <AssistDialogs />
      {/* 云电脑禁止安装安全/虚拟化软件提示（对齐 android NotificationManager） */}
      <BlockAppInstallToast />
    </>
  )
}

export default App
